import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IEmployeeSimple } from '../../models';
import { AlertService, AlertType } from '../../services';
import { Subject, takeUntil } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PersonalCalendarService } from '../../services/personal-calendar.service';
import { IPersonalCalendar } from '../../models/personal-calendar.model';
import { AddPeopleDialogComponent } from '../add-people-dialog/add-people-dialog.component';

@Component({
  selector: 'app-dialog',
  templateUrl: './calendar-create-dialog.component.html',
  styleUrls: ['./calendar-create-dialog.component.scss'],
  standalone: false,
})
export class CalendarCreateDialogComponent
  implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked
{
  data = inject<{
    userId: number;
    calendar?: IPersonalCalendar;
    isEditMode?: boolean;
  }>(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<CalendarCreateDialogComponent>>(MatDialogRef);
  translate = inject(TranslateService);
  dialog = inject(MatDialog);
  private alertService = inject(AlertService);
  private personalCalendarService = inject(PersonalCalendarService);
  private changeDetection = inject(ChangeDetectorRef);

  isEditMode: boolean = false;
  calendar: IPersonalCalendar;
  selectedEmployees: IEmployeeSimple[] = [];
  employeeNames: string[] = [];
  form: FormGroup;
  userId: number;
  isButtonDisabled: boolean = false;

  private destroy$ = new Subject<void>();

  constructor() {
    const translate = this.translate;

    translate.addLangs(['en']);
    translate.setDefaultLang('en');
    const browserLang = translate.getBrowserLang();
    translate.use(browserLang.match(/en/) ? browserLang : 'en');
  }

  ngOnInit(): void {
    this.userId = this.data.userId;
    if (this.data.calendar) {
      this.calendar = this.data.calendar;
    }
    if (this.data.isEditMode) {
      this.isEditMode = this.data.isEditMode;
    }
    this.resetForm();
  }

  ngAfterViewInit() {
    if (this.isEditMode) {
      this.form.setValue({
        title: this.calendar.title,
        description: this.calendar.description,
      });
      this.selectedEmployees = [
        ...(this.calendar?.employees?.sort((a, b) =>
          a.fullName.trim().localeCompare(b.fullName.trim()),
        ) ?? []),
      ];
      this.employeeNames = this.selectedEmployees.map((employee) => employee.fullName);
    }
  }

  ngAfterViewChecked() {
    this.changeDetection.detectChanges();
  }

  resetForm() {
    this.form = new FormGroup({
      title: new FormControl('', Validators.required),
      description: new FormControl(''),
    });
  }

  createCalendar() {
    this.isButtonDisabled = true;
    const payload = {
      employeeId: this.userId,
      title: this.form.controls.title.value,
      description: this.form.controls.description.value,
      employees: [...this.selectedEmployees],
    };

    this.personalCalendarService
      .createPersonalCalendar(this.userId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.isButtonDisabled = false;
          this.personalCalendarService.setListOfCalendars(data);
          this.alertService.showAlert(
            this.translate.instant('HOME.CALENDAR.SUCCESSFULLY_CREATED'),
            AlertType.success,
          );
        },
        (error) => {
          this.alertService.showAlert(error.error, AlertType.error);
          this.isButtonDisabled = false;
        },
        () => {
          this.dialogRef.close();
        },
      );
  }

  updateCalendar() {
    this.isButtonDisabled = true;
    const payload = {
      id: this.calendar.id,
      employeeId: this.userId,
      title: this.form.controls.title.value,
      description: this.form.controls.description.value,
      employees: [...this.selectedEmployees],
    };

    if (this.isEditMode) {
      this.personalCalendarService
        .editPersonalCalendar(this.userId, payload, this.calendar.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (data) => {
            this.personalCalendarService.setListOfCalendars(data);
            this.alertService.showAlert(
              this.translate.instant('ADMIN.LICENSES.SUCCESSFULLY_UPDATED'),
              AlertType.success,
            );
            this.isButtonDisabled = false;
          },
          (error) => {
            this.alertService.showAlert(error.error, AlertType.error);
            this.isButtonDisabled = false;
          },
          () => {
            this.dialogRef.close();
          },
        );
    }
  }

  openAddPeopleDialog() {
    const addPeopleDialogRef = this.dialog.open(AddPeopleDialogComponent, {
      data: {
        employees: this.selectedEmployees,
      },
    });

    addPeopleDialogRef.afterClosed().subscribe((result) => {
      if (result && result.employees) {
        this.selectedEmployees = result.employees;
        this.employeeNames = result.employees.map((employee: IEmployeeSimple) => employee.fullName);
      }
    });
  }

  deleteEmployee(index: number) {
    this.selectedEmployees.splice(index, 1);
    this.employeeNames.splice(index, 1);
  }

  closeDialog() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
